
import { db, serverTimestamp } from "../../firebaseClient";
import type { ContractDoc } from "../types/legalOps";
import { generateContractNo } from "../utils/idGenerator";
import { createContract, updateContract } from "./contractService";
import * as XLSX from "xlsx";

export const CONTRACT_CSV_HEADERS = [
  'contractNumber',
  'title',
  'counterparty',
  'department',
  'startDate',
  'endDate',
  'status',
  'owner',
  'note'
];

const escapeCsv = (val: any) => {
  if (val === undefined || val === null) return "";
  const s = String(val).replace(/"/g, '""');
  return /[",\n\r]/.test(s) ? `"${s}"` : s;
};

export async function exportContractsToCsv(): Promise<string> {
  const snapshot = await db.collection("legal_contracts").get();
  const contracts = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as ContractDoc));

  const headerRow = CONTRACT_CSV_HEADERS.join(",");

  const rows = contracts.map(c => {
    return CONTRACT_CSV_HEADERS.map(header => {
      let val: any = "";
      switch (header) {
        case 'contractNumber': val = c.contractNo; break;
        case 'title': val = c.title; break;
        case 'counterparty': val = c.counterparty; break;
        case 'department': val = c.ourDept || c.department; break;
        case 'startDate': val = c.periodStart || c.signDate; break;
        case 'endDate': val = c.periodEnd; break;
        case 'status': val = c.status; break;
        case 'owner': val = c.businessOwner || c.ownerName; break;
        case 'note': val = c.notes; break;
      }
      return escapeCsv(val);
    }).join(",");
  });

  return "\uFEFF" + [headerRow, ...rows].join("\r\n");
}

export async function importContractsFromCsv(
  file: File
): Promise<{ created: number; updated: number; skipped: number; }> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const data = e.target?.result;
        // Using XLSX for robust CSV parsing
        const workbook = XLSX.read(data, { type: "array" });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows: any[] = XLSX.utils.sheet_to_json(firstSheet);

        if (rows.length === 0) {
            throw new Error("CSV 檔案內容為空或無法解析");
        }

        let created = 0;
        let updated = 0;
        let skipped = 0;

        const existingSnap = await db.collection("legal_contracts").get();
        const existingDocs = existingSnap.docs.map(d => ({id: d.id, ...d.data()} as ContractDoc));

        for (const row of rows) {
           const payload: Partial<ContractDoc> = {};
           
           // Map CSV columns to Firestore fields based on user request
           // CSV: contractNumber,title,counterparty,department,startDate,endDate,status,owner,note
           
           payload.contractNo = row['contractNumber'];
           payload.title = row['title'];
           
           if (!payload.title) { 
               skipped++; 
               continue; 
           }

           payload.counterparty = row['counterparty'] || "";
           payload.ourDept = row['department'] || "";
           payload.department = payload.ourDept; // Sync fields
           
           const start = row['startDate']; // Assuming format YYYY-MM-DD
           const end = row['endDate'];     // Assuming format YYYY-MM-DD
           
           payload.periodStart = start || "";
           payload.signDate = start || ""; 
           payload.periodEnd = end || "";

           payload.status = row['status'] || "草稿";
           
           const ownerVal = row['owner'];
           if (ownerVal) {
               payload.businessOwner = ownerVal;
               payload.ownerName = ownerVal;
           }

           payload.notes = row['note'] || "";
           payload.riskLevel = 'medium'; // Default if not provided

           // Logic: Update if contractNo exists, else Create
           const exists = payload.contractNo 
              ? existingDocs.find(d => d.contractNo === payload.contractNo) 
              : null;

           if (exists) {
             // Update existing
             await updateContract(exists.id!, payload);
             updated++;
           } else {
             // Create new
             // Generate ID if missing in CSV
             if (!payload.contractNo) {
                payload.contractNo = generateContractNo(payload.signDate, existingDocs);
             }
             
             // Add to local list to ensure unique sequence if generating multiple in one go
             existingDocs.push({ ...payload, id: 'temp', contractNo: payload.contractNo } as ContractDoc);
             
             // Use service to create (triggers Calendar Sync)
             await createContract(payload as ContractDoc);
             created++;
           }
        }
        resolve({ created, updated, skipped });
      } catch(err: any) {
        console.error(err);
        reject(new Error("CSV 解析失敗: " + (err.message || "格式錯誤")));
      }
    };
    reader.onerror = () => reject(new Error("檔案讀取失敗"));
    reader.readAsArrayBuffer(file);
  });
}
